import { ChangeEvent, useEffect, useState } from 'react';
import {
    ComputeBudgetProgram,
    Connection,
    Keypair,
    LAMPORTS_PER_SOL,
    PublicKey,
    TransactionInstruction,
    TransactionMessage,
    VersionedTransaction,
} from '@solana/web3.js';
import { useSolana } from '../hooks/useSolana';
import { useSplToken } from '../hooks/useSplToken';
import { useClipboard } from '../hooks/useClipboard';
import {
    createAssociatedTokenAccountIdempotentInstruction,
    createCloseAccountInstruction,
    createTransferInstruction,
    getAssociatedTokenAddressSync,
} from '@solana/spl-token';
import { useJito } from '../hooks/useJito';
import { LOOKUP_TABLE_CACHE, LiquidityPoolKeysV4, TxVersion, WSOL, buildSimpleTransaction } from '@raydium-io/raydium-sdk';
import { removeKeypairDuplicates } from '../utils/remove-keypair-duplicates';
import getRandomizedArray from '../utils/get-randomized-array';

import '../TokenMixerSales.css';
import {
    tokenMixerSalesBaseJitoTipDafault,
    tokenMixerSalesMixerJitoTipDafault,
    tokenMixerSalesSalesJitoTipDafault,
    volumeSellerPumpswapMaxPriorityFeeRPCLamports,
    volumeSellerPumpswapMinPriorityFeeRPCLamports,
} from '../../ConfigFees';
import { usePumpswap } from '../hooks/usePumpswap';
import BN from 'bn.js';
import { useUrlParams } from '../hooks/useUrlParams';
import { volumeMakerV2CheckPoolBalanceSolUi } from '../../ConfigConstants';

export function TokenMixerSales({
    connections,
    connection,
    funder,
    bundleBuyKeypairs,
    holderKeypair,
    deployerKeypair,
    mixKeypairs,
    deployedToken,
    allWallets,
    allWalletRoles,
    parsedCsvData,
    initialTotalSolBalance,
    logMessage,
    volumeBuyWallets,
    receivePoolKeys,
    updatedAt,
}: {
    connections: Connection[];
    connection: Connection;
    funder: Keypair;
    bundleBuyKeypairs: Keypair[];
    mixKeypairs: Keypair[];
    holderKeypair: Keypair;
    deployerKeypair: Keypair;
    deployedToken: string;
    allWallets: Keypair[];
    allWalletRoles: string[];
    parsedCsvData: any[][] | null;
    initialTotalSolBalance: number;
    logMessage: (message: string, type?: 'success' | 'error') => void;
    volumeBuyWallets: Keypair[];
    receivePoolKeys: () => Promise<LiquidityPoolKeysV4>;
    updatedAt: string;
}) {
    const { getMultipleSolBalances, executeInstructionsRpc } = useSolana();
    const {
        createTokenAccountsWithJito,
        getTokenAccountBalance,
        getWalletTokenAccountByMint,
        getTokenDecimals,
        getWalletsTokenStringBalances,
        getTokenAccountsBalances,
        getCreateTokenAccountsInstructions,
    } = useSplToken();
    const { getBuyIx, getPoolId, calculateBaseFromQuote, getPoolBaseTokenBalance } = usePumpswap();
    const { spamAndConfirmBundle } = useJito();
    const { copyToClipboard } = useClipboard();
    const { autoAction } = useUrlParams();
    const [tokenDecimals, setTokenDecimals] = useState<number>(6);

    const [wallets, setWallets] = useState<string[]>([]);

    const [roles, setRoles] = useState<string[]>([]);
    useEffect(() => {
        setWallets(allWallets.map((keypair) => keypair.publicKey.toBase58()));

        setRoles(
            allWalletRoles.map((role) => {
                if (role === 'MIX') {
                    return `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}${mixCounter++}`;
                } else if (role === 'BUNDLEBUY') {
                    return `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}${bundlebuyCounter++}`;
                } else {
                    return `${role.charAt(0).toUpperCase()}${role.slice(1).toLowerCase()}`;
                }
            }),
        );
    }, [allWallets, allWalletRoles]);

    let mixCounter = 1;
    let bundlebuyCounter = 1;

    const [jitoTip, setJitoTip] = useState<number>(tokenMixerSalesBaseJitoTipDafault);

    const [randomSellLock0, setRandomSellLock0] = useState(false);
    const [randomSellInterval0, setRandomSellInterval0] = useState<number>(2);
    const [randomSellPercent0, setRandomSellPercent0] = useState<number>(20);
    const [randomSellIntervalTimer0, setRandomSellIntervalTimer0] = useState<NodeJS.Timer | null>(null);

    const [randomSellLock1, setRandomSellLock1] = useState(false);
    const [randomSellInterval1, setRandomSellInterval1] = useState<number>(5);
    const [randomSellPercent1, setRandomSellPercent1] = useState<number>(100);
    const [randomSellIntervalTimer1, setRandomSellIntervalTimer1] = useState<NodeJS.Timer | null>(null);

    const [randomWalletsInput, setRandomWalletsInput] = useState<number>(1);

    const [jitoTipMixer, setJitoTipMixer] = useState<number>(tokenMixerSalesMixerJitoTipDafault);
    const [jitoTipSales, setJitoTipSales] = useState<number>(tokenMixerSalesSalesJitoTipDafault);
    const [allSolBalances, setAllSolBalances] = useState<number[]>([]);
    const [allTokenBalances, setAllTokenBalances] = useState<number[]>([]);

    const [selectedWalletAddress, setSelectedWalletAddress] = useState(wallets[0]);
    const [amountToSell, setAmountToSell] = useState<number>(0);
    const [walletSelectedCount, setWalletSelectedCount] = useState<number>(0);
    const [walletSelected, setWalletSelected] = useState<boolean[]>(new Array(wallets.length).fill(false));
    const [totalMintedTokenBalance, setTotalMintedTokenBalance] = useState<number>(0);
    const [mintedTokenSupplyPercent, setMintedTokenSupplyPercent] = useState<number>(0);
    const [totalSolBalance, setTotalSolBalance] = useState<number>(0);
    const [solProfit, setSolProfit] = useState<number>(0);

    const [sourceWalletBundlebuy, setSourceWalletBundlebuy] = useState<boolean>(false);
    const [sourceWalletHolder, setSourceWalletHolder] = useState<boolean>(true);
    const [sourceWalletDeployer, setSourceWalletDeployer] = useState<boolean>(false);
    const algorithmOptions = [0, 1, 2, 3];
    const algorithmLabels = ['Default'];
    const [algorithm, setAlgorithm] = useState(algorithmOptions[0]);

    const [onlyPositiveBalances, setOnlyPositiveBalances] = useState<boolean>(true);

    const [autoActionStarted, setAutoActionStarted] = useState<boolean>(false);

    useEffect(() => {
        if (!deployedToken) return;
        if (autoAction !== 'mixerSell') return;
        if (autoActionStarted) return;

        const timeDifference = Date.now() - new Date(updatedAt).getTime();
        if (timeDifference > 30000) return;

        logMessage('Starting auto action for Mixer.');
        setAutoActionStarted(true);
        switchRandomSell0();
    }, [deployedToken, autoAction, autoActionStarted]);

    const randomWalletsInputChange = (event: ChangeEvent<HTMLInputElement>) => {
        let walletsNumber = Number(event.target.value);
        const totalWallets = allWallets.length;
        if (onlyPositiveBalances) {
            const positiveBalances = allTokenBalances.filter((balance) => balance > 0).length;
            if (walletsNumber > positiveBalances) {
                walletsNumber = positiveBalances;
            }
        } else if (walletsNumber > totalWallets) {
            walletsNumber = totalWallets;
        }

        setRandomWalletsInput(walletsNumber);
    };

    useEffect(() => {
        const count = walletSelected.filter(Boolean).length;
        setWalletSelectedCount(count);
    }, [walletSelected]);

    function selectRandomWallets() {
        const randomWallets: number[] = [];
        const totalWallets = allWallets.length;

        const positiveBalances = allTokenBalances.filter((balance) => balance > 0).length;
        if (randomWalletsInput > positiveBalances && onlyPositiveBalances) {
            console.error('Input cannot be greater than the number of available wallets.');
            return;
        }

        while (randomWallets.length < randomWalletsInput) {
            const randomIndex = Math.floor(Math.random() * totalWallets);
            if (!randomWallets.includes(randomIndex) && (!onlyPositiveBalances || allTokenBalances[randomIndex] > 0)) {
                randomWallets.push(randomIndex);
            }
        }

        const newSelectedWallets = new Array(totalWallets).fill(false);
        randomWallets.forEach((index) => {
            newSelectedWallets[index] = true;
        });

        setWalletSelected(newSelectedWallets);
    }

    const selectedWalletsChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
        const newSelectedWallets = [...walletSelected];
        newSelectedWallets[index] = event.target.checked;
        setWalletSelected(newSelectedWallets);
    };

    const jitoTipMixerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setJitoTipMixer(Number(event.target.value));
    };

    const jitoTipSalesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setJitoTipSales(Number(event.target.value));
    };

    const sourceWalletBundlebuyChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setSourceWalletBundlebuy(checked);
    };

    const sourceWalletHolderChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setSourceWalletHolder(checked);
    };

    const sourceWalletDeployerChange = ({ target: { checked } }: { target: { checked: boolean } }) => {
        setSourceWalletDeployer(checked);
    };

    const algorithmChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setAlgorithm(parseInt(event.target.value, 10));
    };

    const mixerWalletAddressChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedWalletAddress(event.target.value);
        setAmountToSell(allTokenBalances[wallets.findIndex((wallet) => wallet === event.target.value)]);
    };

    const amountToSellChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAmountToSell(Number(event.target.value));
    };

    const walletSelectedChange =
        (index: number) =>
        ({ target: { checked } }: { target: { checked: boolean } }) => {
            const newWalletSelected = [...walletSelected];

            newWalletSelected[index] = checked;

            setWalletSelected(newWalletSelected);
        };

    useEffect(() => {
        const count = walletSelected.filter(Boolean).length;
        setWalletSelectedCount(count);
    }, [walletSelected]);

    useEffect(() => {
        function removeDuplicateBalances(wallets: Keypair[], balances: number[]): number[] {
            const seen = new Set<string>();
            const uniqueIndices: number[] = [];

            wallets.forEach((wallet, index) => {
                if (!seen.has(wallet.publicKey.toBase58())) {
                    seen.add(wallet.publicKey.toBase58());
                    uniqueIndices.push(index);
                }
            });

            return balances.filter((_, index) => uniqueIndices.includes(index));
        }

        try {
            const filteredSolBalances = removeDuplicateBalances(allWallets, allSolBalances);
            const totalSolBalance = Number(filteredSolBalances.reduce((acc, balance) => acc + balance, 0) / LAMPORTS_PER_SOL);
            const totalProfit = totalSolBalance - initialTotalSolBalance / LAMPORTS_PER_SOL;
            setSolProfit(Number(totalProfit.toFixed(2)));
            setTotalSolBalance(Number(totalSolBalance.toFixed(2)));

            if (deployedToken !== '') {
                const filteredTokenBalances = removeDuplicateBalances(allWallets, allTokenBalances);
                const totalTokenBalance = Number(filteredTokenBalances.reduce((acc, balance) => acc + Number(balance), 0).toFixed(2));
                setTotalMintedTokenBalance(totalTokenBalance);

                connection
                    .getTokenSupply(new PublicKey(deployedToken), 'processed')
                    .then((supply) => {
                        if (supply !== null && supply.value.uiAmount !== 0 && supply.value.uiAmount !== null) {
                            setMintedTokenSupplyPercent(Number(((totalMintedTokenBalance / supply.value.uiAmount) * 100).toFixed(2)));
                        }
                        const decimals = supply.value.decimals;
                        setTokenDecimals(decimals);
                    })
                    .catch((error) => {
                        console.error('Error fetching token supply:', error);
                    });
            }
        } catch (error) {
            console.error(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSolBalances, allTokenBalances, deployedToken]);

    useEffect(() => {
        const fetchBalances = async () => {
            try {
                const solBalances = await getMultipleSolBalances(
                    connection,
                    allWallets.map((keypair) => keypair.publicKey),
                );
                setAllSolBalances(solBalances);

                let tokenDecimals = 0;
                if (deployedToken !== '') {
                    tokenDecimals = await getTokenDecimals(connection, new PublicKey(deployedToken));
                }

                const getWalletsTokenBalances =
                    deployedToken === ''
                        ? Array(allWallets.length).fill('-')
                        : (
                              await getWalletsTokenStringBalances(
                                  connection,
                                  allWallets.map((wallet) => wallet.publicKey),
                                  new PublicKey(deployedToken),
                              )
                          ).map((balance) => (Number(balance) / 10 ** tokenDecimals).toFixed(2));

                //setAmountToSell(getWalletsTokenBalances[allWallets.findIndex((wallet) => wallet.publicKey.toBase58() === selectedWalletAddress)]);
                setAllTokenBalances(getWalletsTokenBalances);
            } catch (error) {
                console.log(error);
            }
        };

        fetchBalances();

        const intervalId = setInterval(fetchBalances, 10000);

        return () => clearInterval(intervalId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allWallets, deployedToken, connection]); //, allWallets, connection, getMultipleSolBalances, getTokenDecimals, getWalletsTokenStringBalances]);

    function selectAllWallets() {
        if (walletSelectedCount === wallets.length) {
            setWalletSelected(new Array(wallets.length).fill(false));
        } else {
            setWalletSelected(new Array(wallets.length).fill(true));
        }
    }

    const switchRandomSell0 = async () => {
        if (randomSellLock0) {
            console.debug('Random sell locked');
            return;
        }

        setRandomSellLock0(true);

        if (randomSellIntervalTimer0) {
            clearInterval(randomSellIntervalTimer0);
            setRandomSellIntervalTimer0(null);
        } else if (!deployedToken) logMessage('Deployed token not provided', 'error');
        else {
            setRandomSellIntervalTimer0(
                setInterval(async () => {
                    try {
                        const tokenBalances = await getWalletsTokenStringBalances(
                            connection,
                            mixKeypairs.map((wallet) => wallet.publicKey),
                            new PublicKey(deployedToken),
                        );
                        console.debug('token balances', tokenBalances);
                        const walletsWithPositiveBalance = mixKeypairs.filter((wallet, index) => tokenBalances[index] !== '0');

                        console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                        if (walletsWithPositiveBalance.length == 0) {
                            console.warn('No wallets with positive balance');
                            logMessage('No wallets with positive balance left', 'error');
                            return;
                        }
                        const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                        console.debug('Random index', randomIndex);
                        const randomWallet = walletsWithPositiveBalance[randomIndex];
                        console.debug('Random wallet', randomWallet.publicKey.toBase58());
                        await sellMultipleWithPercent(connection, [randomWallet], [randomSellPercent0]);
                    } catch (error) {
                        logMessage('Error during random sell', 'error');
                        console.error(error);
                    }
                }, randomSellInterval0 * 1000),
            );

            logMessage('Random sell enabled', 'success');
        }

        setRandomSellLock0(false);
    };

    const switchRandomSell1 = async () => {
        if (randomSellLock1) {
            console.debug('Random sell locked');
            return;
        }

        setRandomSellLock1(true);

        if (randomSellIntervalTimer1) {
            clearInterval(randomSellIntervalTimer1);
            setRandomSellIntervalTimer1(null);
        } else if (!deployedToken) logMessage('Deployed token not provided', 'error');
        else {
            logMessage('Searching for AMM pool...');
            setRandomSellIntervalTimer1(
                setInterval(async () => {
                    try {
                        const tokenBalances = await getWalletsTokenStringBalances(
                            connection,
                            mixKeypairs.map((wallet) => wallet.publicKey),
                            new PublicKey(deployedToken),
                        );
                        console.debug('token balances', tokenBalances);
                        const walletsWithPositiveBalance = mixKeypairs.filter((wallet, index) => tokenBalances[index] !== '0');

                        console.debug('Wallets with positive balance', walletsWithPositiveBalance);
                        if (walletsWithPositiveBalance.length == 0) {
                            console.warn('No wallets with positive balance');
                            logMessage('No wallets with positive balance left', 'error');
                            return;
                        }
                        const randomIndex = Math.floor(Math.random() * (walletsWithPositiveBalance.length - 1));
                        console.debug('Random index', randomIndex);
                        const randomWallet = walletsWithPositiveBalance[randomIndex];
                        console.debug('Random wallet', randomWallet.publicKey.toBase58());
                        await sellMultipleWithPercent(connection, [randomWallet], [randomSellPercent1]);
                    } catch (error) {
                        logMessage('Error during random sell', 'error');
                        console.error(error);
                    }
                }, randomSellInterval1 * 1000),
            );

            logMessage('Random sell enabled', 'success');
        }

        setRandomSellLock1(false);
    };

    async function sellMultipleWithPercent(connection: Connection, wallets: Keypair[], percents: number[]) {
        logMessage('Selling...');
        const balances = (
            await getWalletsTokenStringBalances(
                connection,
                wallets.map((wallet) => wallet.publicKey),
                new PublicKey(deployedToken),
            )
        ).map((balance) => Number(balance));

        const swapValues = balances.map((balance, index) => {
            return Math.floor((balance * percents[index]) / 100);
        });

        for (const [index, wallet] of wallets.entries()) {
            sellSingle(connection, wallet, swapValues[index]);
        }
    }

    async function sellSingle(connection: Connection, wallet: Keypair, tokenAmount: number) {
        try {
            const min = volumeSellerPumpswapMinPriorityFeeRPCLamports;
            const max = volumeSellerPumpswapMaxPriorityFeeRPCLamports;
            const priorityFeeLamports = Math.floor(Math.random() * (max - min) + min);

            const poolId = await getPoolId(deployerKeypair.publicKey, new PublicKey(WSOL.mint), new PublicKey(deployedToken));
            const solAmount = await calculateBaseFromQuote(connection, poolId, new BN(tokenAmount), 15);

            const wsolAta = getAssociatedTokenAddressSync(new PublicKey(WSOL.mint), wallet.publicKey);
            const poolSolBalance = await getPoolBaseTokenBalance(connection, poolId);
            if (Number(poolSolBalance) < volumeMakerV2CheckPoolBalanceSolUi * LAMPORTS_PER_SOL) {
                logMessage(`Pool balance is less than ${volumeMakerV2CheckPoolBalanceSolUi} SOL. Skipping...`, 'error');
                //switchRandomSell();
                return;
            }

            const openWsolAccountIx = createAssociatedTokenAccountIdempotentInstruction(wallet.publicKey, wsolAta, wallet.publicKey, new PublicKey(WSOL.mint));
            const buyix = getBuyIx(
                poolId,
                new PublicKey(WSOL.mint),
                new PublicKey(deployedToken),
                wallet.publicKey,
                BigInt(solAmount.toString()),
                BigInt(tokenAmount),
            );
            const priorityFeeIx = ComputeBudgetProgram.setComputeUnitPrice({ microLamports: priorityFeeLamports });

            const closeAccountIx = createCloseAccountInstruction(wsolAta, wallet.publicKey, wallet.publicKey);

            const message = new TransactionMessage({
                payerKey: wallet.publicKey,
                recentBlockhash: (await connection.getLatestBlockhash()).blockhash,
                instructions: [openWsolAccountIx, buyix, closeAccountIx, priorityFeeIx],
            }).compileToV0Message();
            const buyTx = new VersionedTransaction(message);
            buyTx.sign([wallet]);

            const hash = await connection.sendTransaction(buyTx, { skipPreflight: true });
            logMessage(`Transaction sent: ${hash}, token amount ${tokenAmount / Math.pow(10, 9)}`, 'success');
        } catch (e) {
            console.log(e);
            logMessage(`Error selling wallet from ${wallet.publicKey.toBase58()}`);
        }
    }
    async function sendWave(connection: Connection, parents: Keypair[], sons: Map<string, Keypair[]>, spendPromile: number = 8500) {
        // const transferTransactions: VersionedTransaction[] = [];

        console.log(
            'parents',
            parents.map((keypair) => keypair.publicKey.toBase58()),
        );

        const waveInstructions: TransactionInstruction[] = [];
        const waveSigners: Keypair[] = [];
        for (const wallet of parents) {
            const parentTokenAccount = await getWalletTokenAccountByMint(connection, wallet.publicKey, new PublicKey(deployedToken));
            const parentBalance = (await getTokenAccountBalance(connection, parentTokenAccount[0].pubkey)).amount;
            console.log('parent balance', parentBalance);
            if (parentBalance === 0) {
                continue;
            }
            const children = sons.get(wallet.publicKey.toBase58())!;
            if (children.length === 0) {
                continue;
            }
            const randomSpendArray = getRandomizedArray(spendPromile, children.length, 20);
            console.log('random spend array', randomSpendArray);
            for (const [index, child] of children.entries()) {
                //try {
                console.log(
                    `parent wallet: ${wallet.publicKey.toBase58()} => child: ${child.publicKey.toBase58()} => amount: ${Math.floor(
                        (randomSpendArray[index] * parentBalance) / 10000 / 10 ** 6,
                    )}`,
                );
                const childTokenAccount = getAssociatedTokenAddressSync(new PublicKey(deployedToken), child.publicKey);
                const transferInstruction = createTransferInstruction(
                    parentTokenAccount[0].pubkey,
                    childTokenAccount,
                    wallet.publicKey,
                    Math.floor((randomSpendArray[index] * parentBalance) / 10000),
                );

                waveInstructions.push(transferInstruction);
                waveSigners.push(wallet);
            }
        }

        return { waveInstructions, waveSigners };
    }

    async function mixTokenThreeWaves() {
        const sons = new Map<string, Keypair[]>();
        // try {
        const wave1Size = Math.ceil(Math.ceil(mixKeypairs.length / 3));
        const wave2Size = Math.floor(Math.ceil((mixKeypairs.length - wave1Size) / 2));

        let wave0Wallets: Keypair[] = [];

        if (sourceWalletDeployer) {
            wave0Wallets.push(deployerKeypair);
        }
        if (sourceWalletBundlebuy) {
            wave0Wallets.push(...bundleBuyKeypairs);
        }
        if (sourceWalletHolder) {
            wave0Wallets.push(holderKeypair);
        }

        wave0Wallets = removeKeypairDuplicates(wave0Wallets);
        //remove wallets that have 0 balance
        wave0Wallets = wave0Wallets.filter((keypair) => allTokenBalances[allWallets.findIndex((wallet) => wallet.publicKey.equals(keypair.publicKey))] > 0);
        if (wave0Wallets.length === 0) {
            return logMessage('No wallets with balance selected');
        }
        const wave1Wallets = mixKeypairs.slice(0, wave1Size);
        const wave2Wallets = mixKeypairs.slice(wave1Size, wave1Size + wave2Size);
        const wave3Wallets = mixKeypairs.slice(wave1Size + wave2Size);

        console.log(wave0Wallets.map((keypair) => keypair.publicKey.toBase58()));
        console.log(wave1Wallets.map((keypair) => keypair.publicKey.toBase58()));
        console.log(wave2Wallets.map((keypair) => keypair.publicKey.toBase58()));
        console.log(wave3Wallets.map((keypair) => keypair.publicKey.toBase58()));
        for (const wallet of [...wave0Wallets, ...wave1Wallets, ...wave2Wallets]) {
            sons.set(wallet.publicKey.toBase58(), []);
        }

        function distributeSons(currentWaveWallets: Keypair[], nextWaveWallets: Keypair[]) {
            const numWallets = currentWaveWallets.length;
            const numSons = nextWaveWallets.length;
            const baseSonsNumber = Math.floor(numSons / numWallets);
            const remainder = numSons % numWallets;

            for (let i = 0; i < numWallets; i++) {
                const sonsStart = i * baseSonsNumber + Math.min(i, remainder);
                const sonsEnd = sonsStart + baseSonsNumber + (i < remainder ? 1 : 0);
                const localSons = nextWaveWallets.slice(sonsStart, sonsEnd);
                sons.set(currentWaveWallets[i].publicKey.toBase58(), localSons);
            }
        }

        distributeSons(wave0Wallets, wave1Wallets);
        distributeSons(wave1Wallets, wave2Wallets);
        distributeSons(wave2Wallets, wave3Wallets);

        logMessage('Creating token accounts...');
        await createTokenAccountsWithJito(
            connection,
            [...wave0Wallets, ...mixKeypairs],
            new PublicKey(deployedToken),
            'confirmed',
            jitoTipMixer * LAMPORTS_PER_SOL,
            logMessage,
        );
        logMessage('Token accounts created successfully...');

        logMessage('Sending wave 1...');
        await sendWave(connection, wave0Wallets, sons, 10000);
        logMessage('Sending wave 2...');
        await sendWave(connection, wave1Wallets, sons);
        logMessage('Sending wave 3...');
        await sendWave(connection, wave2Wallets, sons);

        logMessage('Token mixing bundles sent and confirmed.', 'success');
        // } catch (error) {
        //     logMessage(`Error: ${error}`);
        // }
    }

    async function mixTokenOneWave() {
        logMessage('Mixing token in one wave...');
        const sons = new Map<string, Keypair[]>();
        // try {
        const wave1Size = mixKeypairs.length;

        let wave0Wallets: Keypair[] = [];

        if (sourceWalletDeployer) {
            wave0Wallets.push(deployerKeypair);
        }
        if (sourceWalletBundlebuy) {
            wave0Wallets.push(...bundleBuyKeypairs);
        }
        if (sourceWalletHolder) {
            wave0Wallets.push(holderKeypair);
        }

        wave0Wallets = removeKeypairDuplicates(wave0Wallets);
        //remove wallets that have 0 balance
        wave0Wallets = wave0Wallets.filter((keypair) => allTokenBalances[allWallets.findIndex((wallet) => wallet.publicKey.equals(keypair.publicKey))] > 0);
        if (wave0Wallets.length === 0) {
            return logMessage('No wallets with balance selected');
        }
        const wave1Wallets = mixKeypairs.slice(0, wave1Size);

        console.log(wave0Wallets.map((keypair) => keypair.publicKey.toBase58()));
        for (const wallet of [...wave0Wallets]) {
            sons.set(wallet.publicKey.toBase58(), []);
        }

        function distributeSons(currentWaveWallets: Keypair[], nextWaveWallets: Keypair[]) {
            const numWallets = currentWaveWallets.length;
            const numSons = nextWaveWallets.length;
            const baseSonsNumber = Math.floor(numSons / numWallets);
            const remainder = numSons % numWallets;

            for (let i = 0; i < numWallets; i++) {
                const sonsStart = i * baseSonsNumber + Math.min(i, remainder);
                const sonsEnd = sonsStart + baseSonsNumber + (i < remainder ? 1 : 0);
                const localSons = nextWaveWallets.slice(sonsStart, sonsEnd);
                sons.set(currentWaveWallets[i].publicKey.toBase58(), localSons);
            }
        }

        distributeSons(wave0Wallets, wave1Wallets);

        const { transactionsFullInstructions: createAccountsInstructions, signers: createAccountsSigners } = await getCreateTokenAccountsInstructions(
            connection,
            [...wave0Wallets, ...mixKeypairs],
            new PublicKey(deployedToken),
        );
        const { waveInstructions, waveSigners } = await sendWave(connection, wave0Wallets, sons, 10000);

        logMessage(`Instruction created, sending bundles...`);
        const ataTxs = await executeInstructionsRpc(connection, [...createAccountsInstructions], [...createAccountsSigners], funder, 50000, false, logMessage);
        const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
        for (const sig of ataTxs) {
            await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: sig }, 'processed');
        }
        logMessage(`Token accounts created successfully...`);

        const sendTxs = await executeInstructionsRpc(connection, [...waveInstructions], [...waveSigners], funder, 50000, false, logMessage);
        for (const sig of sendTxs) {
            await connection.confirmTransaction({ blockhash: blockhash, lastValidBlockHeight: lastValidBlockHeight, signature: sig }, 'processed');
        }
        logMessage('Token mixing bundles sent and confirmed.', 'success');
        // } catch (error) {
        //     logMessage(`Error: ${error}`);
        // }
    }

    async function sellToken(connection: Connection, wallets: Keypair[], amountsToSell: number[]) {
        try {
            logMessage('Selling token...');
            for (const [index, wallet] of wallets.entries()) {
                if (amountsToSell[index] === 0) continue;

                sellSingle(connection, wallet, amountsToSell[index]);
            }
            logMessage(`All spam bundles sent`, 'success');
        } catch (error) {
            logMessage(`Error: ${error}`);
        }
    }

    async function sellCustomPercent(connection: Connection, walletsFrom: Keypair[], tokenMint: PublicKey, percent: number) {
        if (percent <= 0 || percent > 100) {
            return logMessage(`Invalid percent`);
        }
        logMessage(`Selling ${percent}% from ${walletsFrom.length} wallet(s)...`);

        const tokenAccounts = (await Promise.all(walletsFrom.map((wallet) => getWalletTokenAccountByMint(connection, wallet.publicKey, tokenMint)))).filter(
            (account) => account.length > 0,
        );
        console.log('Token accounts', tokenAccounts);
        const balances = await getTokenAccountsBalances(
            connection,
            tokenAccounts.map((account) => account[0].pubkey),
        );
        sellToken(
            connection,
            walletsFrom,
            balances.map((balance) => Math.floor((Number(balance) * percent) / 100)),
        );
    }

    async function createTokenAccounts(connection: Connection, wallets: Keypair[], mint: PublicKey) {
        logMessage('Creating token accounts...');
        await createTokenAccountsWithJito(connection, wallets, mint, 'confirmed', jitoTipMixer * LAMPORTS_PER_SOL, logMessage);
        logMessage('Token accounts created successfully...', 'success');
    }

    return (
        <section>
            <div className='headline-container'>
                <h2>Token Mixer</h2>
                <label>
                    JITO Mix tip:
                    <input type='number' name='jitoTipMixer' value={jitoTipMixer} onChange={jitoTipMixerChange} />
                </label>
            </div>
            <section>
                <div className='mix-algorithm-container'>
                    <label className='algorithm-select'>
                        <b>Algorithm</b>
                        <select value={algorithm} onChange={algorithmChange}>
                            {algorithmOptions.map((algorithm, index) => (
                                <option value={algorithm} key={algorithm}>
                                    {algorithmLabels[index]}
                                </option>
                            ))}
                        </select>
                    </label>
                    <div className='algorithm-source-wallets-container'>
                        <b>Source Wallets</b>
                        <label className='algorithm-source-wallets'>
                            <input type='checkbox' checked={sourceWalletBundlebuy} onChange={sourceWalletBundlebuyChange} disabled={!bundleBuyKeypairs[0]} />
                            <span>BundleBuy</span>
                        </label>
                        <label className='algorithm-source-wallets'>
                            <input type='checkbox' checked={sourceWalletHolder} onChange={sourceWalletHolderChange} disabled={!holderKeypair} />
                            <span>Holder</span>
                        </label>
                        <label className='algorithm-source-wallets'>
                            <input type='checkbox' checked={sourceWalletDeployer} onChange={sourceWalletDeployerChange} disabled={!parsedCsvData} />
                            <span>Deployer</span>
                        </label>
                    </div>
                </div>
                <div className='token-mixer-buttons-container'>
                    <button className='button-accent button-300' onClick={() => mixTokenThreeWaves()} disabled={true}>
                        MIX 3 WAVES
                    </button>
                    <button
                        className='buttin-accent button-300'
                        onClick={() => createTokenAccounts(connection, allWallets, new PublicKey(deployedToken))}
                        disabled={!deployedToken}
                    >
                        Create Token Accounts
                    </button>
                    <button
                        className='buttin-accent button-300'
                        onClick={() =>
                            createTokenAccounts(connection, [...bundleBuyKeypairs, deployerKeypair, funder, holderKeypair], new PublicKey(deployedToken))
                        }
                        disabled={!deployedToken}
                    >
                        Create Bundle Accounts
                    </button>
                    <button className='buttin-accent button-300' onClick={() => mixTokenOneWave()} disabled={!deployedToken}>
                        MIX 1 WAVE
                    </button>
                </div>
            </section>
            <h2>Token Seller Single</h2>
            <section className='token-mixer-sales'>
                <label>
                    Wallet
                    <select className='token-mixer-sales-select' value={selectedWalletAddress} onChange={mixerWalletAddressChange}>
                        {wallets.map((wallet, index) => (
                            <option value={wallet} key={wallet + index}>
                                {`${roles[index]} - ${wallet}`}
                            </option>
                        ))}
                    </select>
                </label>
                <input type='number' value={amountToSell} onChange={amountToSellChange} />
                <div className='token-seller-buttons-container'>
                    <button
                        onClick={() => {
                            sellCustomPercent(
                                connection,
                                [allWallets.find((keypair) => keypair.publicKey.toBase58() === selectedWalletAddress)!],
                                new PublicKey(deployedToken),
                                100,
                            );
                        }}
                    >
                        100%
                    </button>
                    <button
                        onClick={() => {
                            sellCustomPercent(
                                connection,
                                [allWallets.find((keypair) => keypair.publicKey.toBase58() === selectedWalletAddress)!],
                                new PublicKey(deployedToken),
                                50,
                            );
                        }}
                    >
                        50%
                    </button>
                    <button
                        onClick={() => {
                            sellCustomPercent(
                                connection,
                                [allWallets.find((keypair) => keypair.publicKey.toBase58() === selectedWalletAddress)!],
                                new PublicKey(deployedToken),
                                25,
                            );
                        }}
                    >
                        25%
                    </button>
                    <button
                        onClick={() => {
                            sellCustomPercent(
                                connection,
                                [allWallets.find((keypair) => keypair.publicKey.toBase58() === selectedWalletAddress)!],
                                new PublicKey(deployedToken),
                                10,
                            );
                        }}
                    >
                        10%
                    </button>
                    <button
                        onClick={() => {
                            sellCustomPercent(
                                connection,
                                [allWallets.find((keypair) => keypair.publicKey.toBase58() === selectedWalletAddress)!],
                                new PublicKey(deployedToken),
                                1,
                            );
                        }}
                    >
                        1%
                    </button>
                    <button
                        onClick={() =>
                            sellToken(
                                connection,
                                [allWallets.find((keypair) => keypair.publicKey.toBase58() === selectedWalletAddress)!],
                                [amountToSell * 10 ** tokenDecimals],
                            )
                        }
                    >
                        Custom
                    </button>
                </div>
            </section>
            <div className='headline-container'>
                <h2>Token Seller Multi</h2>
                <div>
                    <span>{walletSelectedCount}</span> Wallet Selected
                </div>
                <label>
                    JITO Sell tip:
                    <input type='number' name='jitoTipSales' value={jitoTipSales} onChange={jitoTipSalesChange} />
                </label>
            </div>
            <section className='enable-pecent-sel-random'>
                <div className='switch-percent-time'>
                    <div className='enableAutosell'>
                        <label className='switch'>
                            <input
                                type='checkbox'
                                checked={randomSellIntervalTimer0 != null}
                                onClick={switchRandomSell0}
                                disabled={!deployedToken || volumeBuyWallets.length === 0}
                            />
                            <span className='slider'></span>
                        </label>
                    </div>
                    <div className='interval'>
                        <input
                            type={'number'}
                            value={randomSellInterval0}
                            onChange={(event) => setRandomSellInterval0(parseInt(event.target.value) || 5)}
                            disabled={randomSellIntervalTimer0 != null}
                        />
                        sec
                    </div>
                    <div className='percent'>
                        <input
                            type={'number'}
                            value={randomSellPercent0}
                            onChange={(event) => setRandomSellPercent0(parseInt(event.target.value) || 10)}
                            disabled={randomSellIntervalTimer0 != null}
                        />
                        %
                    </div>
                </div>
                <div className='sel-rand-wal'>
                    <button className='button-accent' onClick={selectRandomWallets}>
                        SELECT RANDOM <input type='number' value={randomWalletsInput} onChange={randomWalletsInputChange} /> WALLETS
                    </button>
                </div>
            </section>
            <section className='ref-sel-num-btn'>
                <div className='switch-percent-time'>
                    <div className='enableAutosell'>
                        <label className='switch'>
                            <input
                                type='checkbox'
                                checked={randomSellIntervalTimer1 != null}
                                onClick={switchRandomSell1}
                                disabled={!deployedToken || volumeBuyWallets.length === 0}
                            />
                            <span className='slider'></span>
                        </label>
                    </div>
                    <div className='interval'>
                        <input
                            type={'number'}
                            value={randomSellInterval1}
                            onChange={(event) => setRandomSellInterval1(parseInt(event.target.value) || 5)}
                            disabled={randomSellIntervalTimer0 != null}
                        />
                        sec
                    </div>
                    <div className='percent'>
                        <input
                            type={'number'}
                            value={randomSellPercent1}
                            onChange={(event) => setRandomSellPercent1(parseInt(event.target.value) || 10)}
                            disabled={randomSellIntervalTimer0 != null}
                        />
                        %
                    </div>
                </div>
                <div className='num-btn'>
                    <button
                        onClick={() =>
                            sellCustomPercent(
                                connection,
                                allWallets.filter((_, index) => walletSelected[index]),
                                new PublicKey(deployedToken),
                                100,
                            )
                        }
                    >
                        100%
                    </button>
                    &nbsp;
                    <button
                        onClick={() =>
                            sellCustomPercent(
                                connection,
                                allWallets.filter((_, index) => walletSelected[index]),
                                new PublicKey(deployedToken),
                                50,
                            )
                        }
                    >
                        50%
                    </button>
                    &nbsp;
                    <button
                        onClick={() =>
                            sellCustomPercent(
                                connection,
                                allWallets.filter((_, index) => walletSelected[index]),
                                new PublicKey(deployedToken),
                                25,
                            )
                        }
                    >
                        25%
                    </button>
                </div>
                <div className='ref-all-buttons'>
                    <button className='button-accent'>
                        <div className='icon-ref'></div>
                    </button>
                    <button onClick={selectAllWallets} className='button-accent'>
                        <div className='icon-sel'></div>
                    </button>
                </div>
            </section>
            <div className='mixer-table-wrapper'>
                <table className='parsed-csv-table'>
                    <thead>
                        <tr>
                            <th>Wallet</th>
                            <th>Minted Token Balance</th>
                            <th>SOL Balance</th>
                            <th>Select</th>
                        </tr>
                    </thead>
                    <tbody>
                        {wallets.map((wallet, index) => (
                            <tr key={wallet}>
                                <td className='multi-seller-wallet-column'>
                                    <span className='multi-seller-wallet'>
                                        <b>{roles[index]}</b> - {wallet.substring(0, 5) + '...' + wallet.substring(wallet.length - 5)}
                                    </span>
                                    <button className='copy-button copy-wallets-button' onClick={() => copyToClipboard(wallet)}>
                                        <span className='copy-button-active-effect'></span>
                                    </button>
                                </td>
                                <td>{allTokenBalances[index]}</td>
                                <td>{`${(allSolBalances[index] / LAMPORTS_PER_SOL).toFixed(2)}`}</td>
                                <td>
                                    <input type='checkbox' checked={walletSelected[index]} onChange={walletSelectedChange(index)} />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    {parsedCsvData && (
                        <tfoot>
                            <tr></tr>
                            <tr>
                                <td></td>
                                <td>
                                    Total:
                                    <br />
                                    {totalMintedTokenBalance.toLocaleString()}
                                    <br />
                                    {mintedTokenSupplyPercent} % of Supply
                                </td>
                                <td>
                                    Total:
                                    <br />
                                    {totalSolBalance} SOL
                                    {solProfit ? (
                                        <p>
                                            <span className='sol-profit'>+{solProfit} SOL </span>
                                            Profit
                                        </p>
                                    ) : (
                                        <p>
                                            <span className='no-sol-profit'>{solProfit} SOL </span>
                                            Profit
                                        </p>
                                    )}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </section>
    );
}
